import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { money } from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { BarChart, Donut } from "../../components/Sparkline";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  TrendingUp, Receipt, Users, Truck, Package, AlertCircle, ArrowUpRight,
  CheckCircle2, ArrowRight, UserPlus, PackagePlus, Wallet, Activity,
  ShoppingBag, Sparkles, Clock, BadgeCheck, FileText, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import SellerBillingBanner from "../../components/SellerBillingBanner";

const ACTIVITY_ICON = { order: ShoppingBag, paid: Receipt, shipped: Truck, connected: UserPlus };

export default function SellerDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [orders, setOrders] = useState([]);
  const [confirming, setConfirming] = useState(null);
  const [readyDate, setReadyDate] = useState("");

  const load = () => Promise.all([
    api.get("/dashboard/seller"),
    api.get("/orders"),
  ]).then(([s, o]) => { setD(s.data); setOrders(o.data); }).catch(() => {});

  useEffect(() => { load(); }, []);

  if (!d) return <div className="p-8 text-foreground/60">Loading…</div>;

  const k = d.kpis;
  const p = d.pipeline;
  const pendingOrders = orders.filter(o => o.status === "pending").slice(0, 5);
  const recentOrders  = orders.filter(o => o.status !== "pending").slice(0, 6);
  const greeting = `${k.orders_to_confirm} order${k.orders_to_confirm !== 1 ? "s" : ""} to confirm, ${d.greeting_action_items.to_ship} to ship, ${k.outstanding_count} invoice${k.outstanding_count !== 1 ? "s" : ""} outstanding.`;

  const openConfirm = (o) => {
    setConfirming(o);
    setReadyDate(new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10));
  };

  const submitConfirm = async () => {
    if (!readyDate) return toast.error("Pick a ready-by date");
    try {
      await api.post(`/orders/${confirming.id}/confirm`, {
        expected_ready_at: new Date(readyDate).toISOString(),
        expected_invoice_at: new Date().toISOString(),
      });
      toast.success(`Order ${confirming.code} confirmed · invoice issued`);
      setConfirming(null); setReadyDate("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Confirm failed");
    }
  };

  const totalPipeline = Math.max(1, p.pending + p.processing + p.transit + p.delivered + p.paid);

  return (
    <>
      <PageHeader
        title="Good morning 👋"
        subtitle={greeting}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => nav("/seller/apps")} className="rounded-full" data-testid="dash-apps">
              <Sparkles className="w-4 h-4 mr-1.5" />Apps
            </Button>
            <Button onClick={() => nav("/seller/buyers")} className="rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="dash-invite-buyer-cta">
              <UserPlus className="w-4 h-4 mr-1.5" />Invite buyer
            </Button>
          </div>
        }
      />

      <div className="p-8 space-y-6">

        {/* SaaS subscription nudge — only renders when status needs attention */}
        <SellerBillingBanner user={user} />

        {/* High-priority alert */}
        {(d.at_risk_receivables?.length > 0 || pendingOrders.length >= 5) && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 flex items-start gap-4" data-testid="action-banner">
            <div className="w-10 h-10 rounded-full bg-amber-200 grid place-items-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-800" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-amber-900/70 font-medium">Action needed</div>
              <div className="text-amber-900 font-medium mt-1">
                {d.at_risk_receivables?.length > 0
                  ? <>You have <span className="display">{d.at_risk_receivables.length}</span> overdue receivable{d.at_risk_receivables.length !== 1 ? "s" : ""} totaling <span className="display">{money(d.at_risk_receivables.reduce((s, r) => s + r.amount, 0))}</span>.</>
                  : <>You have <span className="display">{pendingOrders.length}</span> orders waiting on confirmation. Buyers expect a reply within 24h.</>}
              </div>
            </div>
            <Button onClick={() => nav(d.at_risk_receivables?.length > 0 ? "/seller/orders" : "/seller/orders")}
              className="rounded-full bg-amber-900 text-amber-50 hover:bg-amber-900/90">
              {d.at_risk_receivables?.length > 0 ? "Chase now" : "Confirm orders"} <ArrowUpRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        )}

        {/* KPIs row — 6 tiles, mirror buyer dashboard rhythm */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: "Revenue MTD",     val: money(k.revenue_mtd),
              hint: `${k.revenue_change_pct >= 0 ? "↑" : "↓"} ${Math.abs(k.revenue_change_pct)}% MoM`,
              tone: "ink", icon: TrendingUp, testid: "stat-revenue" },
            { label: "Orders to confirm", val: k.orders_to_confirm,
              hint: k.orders_to_confirm > 0 ? "needs reply" : "all clear",
              icon: Clock, testid: "stat-to-confirm" },
            { label: "Outstanding",     val: money(k.outstanding_receivables),
              hint: `${k.outstanding_count} invoice${k.outstanding_count !== 1 ? "s" : ""}`,
              icon: Receipt, testid: "stat-outstanding" },
            { label: "Avg DSO",         val: `${k.avg_dso_days || 0}d`,
              hint: "days to get paid",
              icon: BadgeCheck, testid: "stat-dso" },
            { label: "Active buyers",   val: k.active_buyers,
              hint: `${k.new_buyers_30d} new in 30d`,
              icon: Users, testid: "stat-buyers" },
            { label: "Wallet",          val: money(k.wallet_balance),
              hint: `${money(k.factoring_available)} factoring`,
              icon: Wallet, testid: "stat-wallet" },
          ].map((s) => (
            <div key={s.label}
                 className={`rounded-xl p-4 relative ${s.tone === "ink" ? "card-ink" : "bg-white border border-border"}`}
                 data-testid={s.testid}>
              <s.icon className={`absolute top-4 right-4 w-3.5 h-3.5 ${s.tone === "ink" ? "text-accent/70" : "text-foreground/30"}`} />
              <div className={`text-[10px] uppercase tracking-widest ${s.tone === "ink" ? "text-accent/80" : "text-foreground/50"}`}>{s.label}</div>
              <div className="display text-xl mt-2">{s.val}</div>
              <div className={`text-[10px] mt-0.5 ${s.tone === "ink" ? "text-background/60" : "text-foreground/55"}`}>{s.hint}</div>
            </div>
          ))}
        </div>

        {/* Charts row — Revenue trend + Pipeline */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="display text-lg">Revenue trend</h2>
                <p className="text-xs text-foreground/60">Last 6 months · paid vs outstanding</p>
              </div>
              <div className="flex gap-3 text-xs">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-foreground" />Paid</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-accent" />Outstanding</span>
              </div>
            </div>
            <div className="h-44"><BarChart data={d.revenue_trend} /></div>
          </div>

          <div className="rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="display text-lg">Order pipeline</h2>
              <span className="chip bg-secondary">{totalPipeline} orders</span>
            </div>
            <div className="space-y-3 mt-4 text-sm">
              {[
                { lbl: "Pending",    val: p.pending,    color: "bg-amber-500" },
                { lbl: "Processing", val: p.processing, color: "bg-foreground/40" },
                { lbl: "In transit", val: p.transit,    color: "bg-foreground/60" },
                { lbl: "Delivered",  val: p.delivered,  color: "bg-foreground" },
                { lbl: "Paid",       val: p.paid,       color: "bg-accent" },
              ].map((row) => {
                const pct = (row.val / totalPipeline) * 100;
                return (
                  <div key={row.lbl}>
                    <div className="flex justify-between text-xs">
                      <span>{row.lbl}</span>
                      <span className="mono">{row.val} · {Math.round(pct)}%</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                      <div className={`h-full ${row.color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Quick actions strip — seller equivalents of buyer's */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
          {[
            { label: "Invite buyer",   icon: UserPlus,    to: "/seller/buyers"      },
            { label: "Add product",    icon: PackagePlus, to: "/seller/products"    },
            { label: "Confirm orders", icon: CheckCircle2, to: "/seller/orders",    badge: k.orders_to_confirm },
            { label: "Print labels",   icon: Truck,       to: "/seller/orders",     badge: d.greeting_action_items.to_ship },
            { label: "Grant terms",    icon: BadgeCheck,  to: "/seller/buyers#credit" },
            { label: "Withdraw funds", icon: Wallet,      to: "/seller/wallet" },
          ].map((a) => (
            <button key={a.label} onClick={() => nav(a.to)}
              className="rounded-xl bg-white border border-border p-3.5 text-left hover:border-foreground/40 hover:bg-secondary/40 transition-all group flex items-center gap-3"
              data-testid={`qa-${a.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-9 h-9 rounded-lg bg-secondary group-hover:bg-foreground group-hover:text-background grid place-items-center transition-colors shrink-0">
                <a.icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{a.label}</div>
                <div className="text-[10px] text-foreground/50">Open</div>
              </div>
              {a.badge > 0 && <span className="chip bg-accent text-foreground border border-accent shrink-0">{a.badge}</span>}
            </button>
          ))}
        </div>

        {/* Pending orders — stays prominent because it's the seller's #1 action */}
        {pendingOrders.length > 0 && (
          <div className="rounded-2xl bg-white border border-border p-6" data-testid="pending-orders">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <div className="text-xs uppercase tracking-widest text-amber-700">Action required</div>
                <h2 className="display text-lg mt-1">{k.orders_to_confirm} order{k.orders_to_confirm !== 1 ? "s" : ""} awaiting your confirmation</h2>
                <p className="text-xs text-foreground/55 mt-0.5">Confirm to issue the invoice and let the buyer pay. Buyer is waiting for your ETA.</p>
              </div>
              <span className="chip bg-amber-100 text-amber-800 border border-amber-200">⏳ {k.orders_to_confirm} pending</span>
            </div>
            <div className="space-y-2">
              {pendingOrders.map(o => (
                <div key={o.id} className="rounded-xl border border-border p-4 grid md:grid-cols-12 gap-3 items-center" data-testid={`pending-${o.code}`}>
                  <div className="md:col-span-2 mono text-sm text-foreground/70">{o.code}</div>
                  <div className="md:col-span-3 font-medium">{o.buyer_name}</div>
                  <div className="md:col-span-3 text-xs text-foreground/60 truncate">
                    {o.items?.[0]?.name} {o.items?.length > 1 && `+${o.items.length - 1}`}
                  </div>
                  <div className="md:col-span-2 display">{money(o.total)}</div>
                  <div className="md:col-span-2 text-right">
                    <Button size="sm" onClick={() => openConfirm(o)} data-testid={`confirm-btn-${o.code}`}
                      className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Confirm
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent orders + At-risk receivables + Activity */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="display text-lg">Recent orders</h2>
                <p className="text-xs text-foreground/60">After confirmation</p>
              </div>
              <Link to="/seller/orders" className="text-xs text-foreground/60 hover:text-foreground inline-flex items-center">
                View all <ArrowUpRight className="w-3 h-3 ml-0.5" />
              </Link>
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-foreground/50">
                <tr>
                  <th className="pb-3 font-medium">Order</th>
                  <th className="pb-3 font-medium">Buyer</th>
                  <th className="pb-3 font-medium">Terms</th>
                  <th className="pb-3 font-medium">Total</th>
                  <th className="pb-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {recentOrders.map(o => (
                  <tr key={o.id}>
                    <td className="py-3 mono text-foreground/70">{o.code}</td>
                    <td className="py-3">{o.buyer_name}</td>
                    <td className="py-3 text-xs uppercase">{o.terms}</td>
                    <td className="py-3 display">{money(o.total)}</td>
                    <td className="py-3"><span className="chip bg-secondary">{o.status}</span></td>
                  </tr>
                ))}
                {recentOrders.length === 0 && (
                  <tr><td colSpan={5} className="py-8 text-center text-foreground/60">No confirmed orders yet</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="space-y-5">
            <div className="rounded-2xl bg-white border border-border p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="display text-base">At-risk receivables</h3>
                  <p className="text-[10px] text-foreground/50">{d.at_risk_receivables.length} overdue</p>
                </div>
                <Link to="/seller/orders" className="text-xs text-foreground/60 hover:text-foreground">
                  Chase <ArrowUpRight className="w-3 h-3 inline ml-0.5" />
                </Link>
              </div>
              <div className="divide-y divide-border">
                {d.at_risk_receivables.map(r => (
                  <div key={r.id} className="py-2.5 flex justify-between items-center" data-testid={`risk-${r.code}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium mono">{r.code}</div>
                      <div className="text-[10px] text-foreground/60 truncate">
                        {r.buyer_name} · <span className="text-rose-700">{r.days_overdue}d overdue</span>
                      </div>
                    </div>
                    <span className="display text-sm">{money(r.amount)}</span>
                  </div>
                ))}
                {d.at_risk_receivables.length === 0 && (
                  <div className="py-6 text-center text-foreground/60 text-xs">All caught up 🎉</div>
                )}
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-border p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="display text-base">Catalog health</h3>
                  <p className="text-[10px] text-foreground/50">{k.products_total} SKUs</p>
                </div>
                <Link to="/seller/products" className="text-xs text-foreground/60 hover:text-foreground">
                  Manage <ArrowUpRight className="w-3 h-3 inline ml-0.5" />
                </Link>
              </div>
              <div className="space-y-3 mt-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-foreground/70 inline-flex items-center gap-2">
                    <Package className="w-3.5 h-3.5 text-foreground/50" />Active SKUs
                  </span>
                  <span className="display">{k.products_total}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground/70 inline-flex items-center gap-2">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-600" />Low stock
                  </span>
                  <span className="display text-amber-700">{k.low_stock_skus}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom row: Top buyers + Activity + Wallet */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="display text-base">Top buyers</h2>
              <Link to="/seller/buyers" className="text-xs text-foreground/60 hover:text-foreground">
                All <ArrowUpRight className="w-3 h-3 inline ml-0.5" />
              </Link>
            </div>
            <div className="space-y-3">
              {d.top_buyers.map((b) => {
                const max = d.top_buyers[0]?.amount || 1;
                return (
                  <div key={b.buyer_id || b.name}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="truncate">{b.name}</span>
                      <span className="display text-sm">{money(b.amount)}</span>
                    </div>
                    <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-foreground" style={{ width: `${(b.amount / max) * 100}%` }} />
                    </div>
                    <div className="text-[10px] text-foreground/50 mt-0.5">{b.orders} order{b.orders !== 1 ? "s" : ""}</div>
                  </div>
                );
              })}
              {d.top_buyers.length === 0 && (
                <div className="text-foreground/60 text-sm py-4 text-center">No data yet</div>
              )}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="display text-base">Activity</h2>
              <Activity className="w-4 h-4 text-foreground/40" />
            </div>
            <div className="space-y-3">
              {d.activity.map((a, i) => {
                const I = ACTIVITY_ICON[a.type] || Activity;
                return (
                  <div key={`${a.type}-${a.date}-${i}`} className="flex items-start gap-2.5">
                    <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${a.type === "paid" ? "bg-accent/30" : "bg-secondary"}`}>
                      <I className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium truncate">{a.title}</div>
                      <div className="text-[10px] text-foreground/55 truncate">
                        {a.subtitle} · {a.date ? new Date(a.date).toLocaleDateString() : "—"}
                      </div>
                    </div>
                  </div>
                );
              })}
              {d.activity.length === 0 && (
                <div className="text-foreground/60 text-sm py-4 text-center">No recent activity</div>
              )}
            </div>
          </div>

          {/* Wallet card — analogous to buyer's credit utilization */}
          <div className="card-ink rounded-2xl p-6 relative overflow-hidden flex flex-col">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-accent/15 blur-3xl" />
            <div className="relative">
              <div className="text-xs mono uppercase tracking-widest text-accent/80">Wallet</div>
              <div className="display text-4xl mt-3">{money(k.wallet_balance)}</div>
              <div className="text-xs text-background/70 mt-1">Available to withdraw · D+1 to bank</div>
            </div>
            <div className="mt-6 h-px bg-background/10" />
            <div className="mt-6 space-y-2.5 text-sm relative">
              <div className="flex justify-between">
                <span className="text-background/60">Factoring available</span>
                <span className="display">{money(k.factoring_available)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-background/60">Factoring rate</span>
                <span className="mono">3.0%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-background/60">Coverage</span>
                <span className="mono">up to 95%</span>
              </div>
            </div>
            <Link to="/seller/wallet" className="text-xs mono text-accent/80 hover:text-accent inline-flex items-center mt-5 relative">
              Manage wallet <ArrowUpRight className="w-3 h-3 ml-1" />
            </Link>
          </div>
        </div>
      </div>

      {/* ───── Confirm dialog (unchanged from before) ───── */}
      <Dialog open={!!confirming} onOpenChange={(v) => !v && setConfirming(null)}>
        <DialogContent className="max-w-md">
          {confirming && (
            <>
              <DialogHeader>
                <DialogTitle className="display text-2xl tracking-tight">Confirm order {confirming.code}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-foreground/55 -mt-1">
                Set when the order will be packed and ready for shipment. The buyer will see this date on their invoice and on order tracking.
              </p>
              <div className="mt-4 rounded-xl bg-secondary/30 border border-border p-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-foreground/55">Buyer</span><span className="font-medium">{confirming.buyer_name}</span></div>
                <div className="flex justify-between"><span className="text-foreground/55">Items</span><span>{confirming.items?.length} SKU · {confirming.items?.reduce((s, i) => s + i.qty, 0)} units</span></div>
                <div className="flex justify-between"><span className="text-foreground/55">Total</span><span className="display">{money(confirming.total)}</span></div>
                <div className="flex justify-between"><span className="text-foreground/55">Terms</span><span className="uppercase">{confirming.terms}</span></div>
              </div>
              <div className="mt-4">
                <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-2 block">When will the order be ready to ship?</label>
                <Input type="date" value={readyDate} onChange={e => setReadyDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="h-12 text-lg display" data-testid="ready-date" />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {[2, 5, 7, 14].map(days => (
                    <button key={days} onClick={() => setReadyDate(new Date(Date.now() + days * 86400000).toISOString().slice(0, 10))}
                      className="text-[11px] rounded-full border border-border px-3 py-1 hover:border-foreground/40">
                      In {days} days
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={submitConfirm} data-testid="submit-confirm"
                className="mt-5 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
                Confirm &amp; issue invoice <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <p className="text-[10px] text-center text-foreground/45 mt-2">Buyer will be notified · invoice generated immediately</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
